import { useEffect, useRef, useState, useCallback } from "react";
import { NoiseTexture } from "./NoiseTexture";
import { ChevronDown } from "lucide-react";

interface SpotlightHeroProps {
  teamName: string;
  companyLogoUrl: string;
  headline: string;
  subheadline?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  heroImageUrl?: string | null;
  heroVideoUrl?: string | null;
}

export function SpotlightHero({
  teamName,
  companyLogoUrl,
  headline,
  subheadline,
  primaryColor,
  secondaryColor,
  heroImageUrl,
  heroVideoUrl,
}: SpotlightHeroProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const ticking = useRef(false);

  const onScroll = useCallback(() => {
    if (!ticking.current) {
      ticking.current = true;
      requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        ticking.current = false;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const accentColor = primaryColor ?? "#3B82F6";

  // scrollRatio: 0 at top, 1 when scrolled one full viewport height
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const scrollRatio = Math.min(1, scrollY / vh);

  // Content: fully opaque until 30% scrolled, then fades out
  const contentOpacity = Math.max(0, 1 - Math.max(0, (scrollRatio - 0.3) / 0.4));
  const contentTranslateY = scrollRatio * -60;
  const bgScale = 1 + scrollRatio * 0.15;

  return (
    <section
      ref={scrollRef}
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      {/* Background layer — z-0 */}
      {heroVideoUrl ? (
        <video
          className="absolute inset-0 z-0 w-full h-full object-cover will-change-transform"
          style={{ transform: `scale(${bgScale})` }}
          src={heroVideoUrl}
          autoPlay
          muted
          loop
          playsInline
        />
      ) : heroImageUrl ? (
        <div
          className="absolute inset-0 z-0 bg-cover bg-center will-change-transform"
          style={{
            backgroundImage: `url(${heroImageUrl})`,
            transform: `scale(${bgScale})`,
          }}
        />
      ) : (
        <div
          className="absolute inset-0 z-0 bg-black will-change-transform"
          style={{ transform: `scale(${bgScale})` }}
        />
      )}

      {/* Gradient overlay — z-10 */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background: `linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.2) 60%, transparent 100%)`,
        }}
      />

      {/* Noise texture — z-10 */}
      <div className="z-10">
        <NoiseTexture />
      </div>

      {/* Logo — top center, z-30 */}
      {companyLogoUrl && (
        <div className="absolute top-8 left-0 right-0 z-30 flex justify-center">
          <img
            src={companyLogoUrl}
            alt={teamName}
            className="h-12 md:h-16 w-auto max-w-[240px] object-contain"
          />
        </div>
      )}

      {/* Content — z-30, fully opaque, on top of everything */}
      <div
        className="relative z-30 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-32 will-change-transform"
        style={{
          opacity: contentOpacity,
          transform: `translateY(${contentTranslateY}px)`,
        }}
      >

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white max-w-4xl leading-[1.1]">
          {headline}
        </h1>

        {/* Brand-colored accent bar */}
        <div
          className="w-20 h-1.5 rounded-full mt-6 mb-6"
          style={{ backgroundColor: accentColor }}
        />

        {subheadline && (
          <p className="text-lg md:text-xl text-white max-w-2xl leading-relaxed">
            {subheadline}
          </p>
        )}

        <p className="text-sm text-white uppercase tracking-[0.2em] font-medium mt-6">
          {teamName}
        </p>
      </div>

      {/* Animated scroll indicator — z-30 */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 animate-float"
        style={{ opacity: Math.max(0, 1 - scrollRatio * 4) }}
      >
        <ChevronDown className="h-6 w-6 text-white" />
      </div>
    </section>
  );
}
